import { useEffect } from 'react';

import { useSession, useUserIdFromSession } from './useSession';

/**
 * Where we remember the organization this browser profile most recently had
 * open, per user.
 *
 * The key format predates ENG-5636 — `TenantLayout` wrote it and nothing read
 * it — so it is kept verbatim: values already written by deployed bundles are
 * honored the moment a user picks up the new bundle.
 */
export const lastAccessedTenantStorageKey = (userId: string) =>
  `last-tenant-id-for-user-${userId}`;

/**
 * The scopes the record is kept in, in the order they are consulted.
 *
 * `sessionStorage` is per TAB and answers first. ENG-5545 exists precisely
 * because people keep tabs open on different orgs, so the browser-wide value
 * names whichever org rendered most recently in ANY tab — leaving the admin
 * console in a tab on org A could otherwise land it on org B [ENG-5785].
 *
 * `localStorage` is the browser-wide record, and the only answer a freshly
 * opened tab has — which is the case ENG-5636 is about. It is also the scope
 * already-deployed bundles write, so their values keep being honored.
 */
const STORAGE_SCOPES = ['session', 'local'] as const;

// Reading the property itself throws `SecurityError` in a sandboxed iframe and
// in some private-browsing modes — `typeof` does not guard a getter that throws.
const resolveStorage = (
  scope: (typeof STORAGE_SCOPES)[number],
): Pick<Storage, 'getItem' | 'setItem'> | null => {
  try {
    return (
      (scope === 'session'
        ? globalThis.sessionStorage
        : globalThis.localStorage) ?? null
    );
  } catch {
    return null;
  }
};

const readLastAccessedTenantId = (userId: string): string | null => {
  const key = lastAccessedTenantStorageKey(userId);

  for (const scope of STORAGE_SCOPES) {
    try {
      const recorded = resolveStorage(scope)?.getItem(key);
      if (recorded) {
        return recorded;
      }
    } catch {
      // Fall through to the browser-wide scope.
    }
  }

  return null;
};

/**
 * Records the organization a tenant-scoped route has open, so tenantless
 * routes (`/`, `/settings`, the admin console) can send the user back to it.
 *
 * Mount this once per tenant route subtree, below whatever gate proves the
 * user may actually be here — a tenant the guard rejected, or one that is
 * pending approval, is not somewhere we want to land them next time.
 */
export const useTrackLastAccessedTenant = (tenantId: string) => {
  const userId = useUserIdFromSession();

  useEffect(
    function recordLastAccessedTenantId() {
      const key = lastAccessedTenantStorageKey(userId);

      for (const scope of STORAGE_SCOPES) {
        try {
          resolveStorage(scope)?.setItem(key, tenantId);
        } catch {
          // A denied or over-quota write must not take down the org page that
          // is merely recording where the user is.
        }
      }
    },
    [tenantId, userId],
  );
};

/**
 * The organization a tenantless route should redirect into.
 *
 * The shared session's `x-hasura-tenant-id` claim looks like the obvious
 * answer, and used to be one: it was rewritten on every org switch. Since
 * tenant tokens became per-tab [ENG-5545] nothing writes it any more, and the
 * session refresh explicitly pins it — so it now means "the org you were in
 * when you logged in", frozen for the whole session. Reading it as if it meant
 * "current org" is what made a new tab land on the pre-switch org [ENG-5636].
 *
 * So prefer what the tabs actually recorded, and fall back to the claim, which
 * is still correct at session creation (it is derived from the most recently
 * accessed tenant) and is what these routes did before.
 *
 * Both candidates are membership-checked, because both outlive revoked access:
 * `TenantGuard` denies a non-member and redirects back here, so handing back an
 * org the session no longer allows is an inescapable loop — `refreshAllowedTenants`
 * rebuilds `allowedTenantIds` but re-derives the claim from that same pinned
 * tenant, so it 403s and the claim never moves. Resolving `undefined` instead
 * lets the caller fall through to the organization switcher.
 */
export const useLandingTenantId = (): string | undefined => {
  const session = useSession();
  const claims = session['https://hasura.io/jwt/claims'];
  const claimTenantId = claims['x-hasura-tenant-id'];

  // While impersonating, the pinned claim IS the intended tenant — the admin
  // chose it and ENG-4309 pins it deliberately. The record can't be trusted
  // here either: it is keyed by the impersonated user, so an earlier
  // impersonation of the same user in a different org would win.
  if (session.impersonation) {
    return claimTenantId;
  }

  const recordedTenantId = readLastAccessedTenantId(claims['x-hasura-user-id']);

  const accessible = (tenantId: string | null | undefined) =>
    tenantId && session.allowedTenantIds.includes(tenantId)
      ? tenantId
      : undefined;

  return accessible(recordedTenantId) ?? accessible(claimTenantId);
};
